/** Pure HTTP-level pieces of the WHIP client (RFC 9725), kept free of fetch and RTCPeerConnection. */
import type { IceServerConfig } from '../core/model';

/** Splits a header value on top-level commas, ignoring those inside <...> or "...". */
function splitHeaderList(value: string): string[] {
  const parts: string[] = [];
  let current = '';
  let inAngle = false;
  let inQuote = false;
  for (let i = 0; i < value.length; i += 1) {
    const ch = value[i]!;
    if (inQuote) {
      if (ch === '\\' && i + 1 < value.length) {
        current += ch + value[i + 1]!;
        i += 1;
        continue;
      }
      if (ch === '"') inQuote = false;
    } else if (ch === '"') inQuote = true;
    else if (ch === '<') inAngle = true;
    else if (ch === '>') inAngle = false;
    else if (ch === ',' && !inAngle) {
      parts.push(current);
      current = '';
      continue;
    }
    current += ch;
  }
  parts.push(current);
  return parts.map((p) => p.trim()).filter((p) => p !== '');
}

function splitParams(value: string): string[] {
  const parts: string[] = [];
  let current = '';
  let inQuote = false;
  for (let i = 0; i < value.length; i += 1) {
    const ch = value[i]!;
    if (inQuote && ch === '\\' && i + 1 < value.length) {
      current += value[i + 1]!;
      i += 1;
      continue;
    }
    if (ch === '"') {
      inQuote = !inQuote;
      continue;
    }
    if (ch === ';' && !inQuote) {
      parts.push(current);
      current = '';
      continue;
    }
    current += ch;
  }
  parts.push(current);
  return parts.map((p) => p.trim()).filter((p) => p !== '');
}

/**
 * ICE servers advertised as `Link: <turn:host?transport=udp>; rel="ice-server";
 * username="u"; credential="p"; credential-type="password"` (RFC 9725 §4.6).
 */
export function parseLinkIceServers(header: string | null): IceServerConfig[] {
  if (!header) return [];
  const servers: IceServerConfig[] = [];
  for (const entry of splitHeaderList(header)) {
    const match = /^<([^>]*)>(.*)$/s.exec(entry);
    if (!match) continue;
    const url = match[1]!.trim();
    const params = new Map<string, string>();
    for (const param of splitParams(match[2]!)) {
      const eq = param.indexOf('=');
      if (eq > 0) params.set(param.slice(0, eq).trim().toLowerCase(), param.slice(eq + 1).trim());
    }
    const rel = (params.get('rel') ?? '').toLowerCase().split(/\s+/);
    if (!rel.includes('ice-server') || !/^(stun|stuns|turn|turns):/i.test(url)) continue;
    const credentialType = params.get('credential-type');
    // Only password credentials can be expressed in RTCIceServer.
    if (credentialType !== undefined && credentialType.toLowerCase() !== 'password') continue;
    const server: IceServerConfig = { urls: url };
    const username = params.get('username');
    const credential = params.get('credential');
    if (username !== undefined) server.username = username;
    if (credential !== undefined) server.credential = credential;
    servers.push(server);
  }
  return servers;
}

/** The Location of the created session; relative values resolve against the URL that answered. */
export function resolveResourceUrl(location: string | null, respondingUrl: string): string | null {
  if (location === null || location.trim() === '') return null;
  try {
    const url = new URL(location.trim(), respondingUrl);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null;
  } catch {
    return null;
  }
}

export function isWhipEndpoint(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function isLoopback(hostname: string): boolean {
  const host = hostname.toLowerCase().replace(/^\[|\]$/g, '');
  return host === 'localhost' || host.endsWith('.localhost') || host === '::1' || /^127(\.\d{1,3}){3}$/.test(host);
}

/**
 * A bearer token sent over plain HTTP is readable by anyone on the path. Only
 * loopback is exempt, for local media servers during development.
 */
export function isTokenSafeEndpoint(endpoint: string): boolean {
  try {
    const url = new URL(endpoint);
    return url.protocol === 'https:' || (url.protocol === 'http:' && isLoopback(url.hostname));
  } catch {
    return false;
  }
}

/**
 * The token belongs to the endpoint the operator configured. The session URL
 * comes from the server's Location header and may name any host, so the token
 * accompanies PATCH and DELETE only when that URL is on the endpoint's origin.
 */
export function tokenForResource(
  resourceUrl: string,
  endpoint: string,
  bearerToken: string | undefined,
): string | undefined {
  try {
    return new URL(resourceUrl).origin === new URL(endpoint).origin ? bearerToken : undefined;
  } catch {
    return undefined;
  }
}

export function whipHeaders(contentType: string | null, bearerToken: string | undefined): Record<string, string> {
  const headers: Record<string, string> = {};
  if (contentType !== null) headers['Content-Type'] = contentType;
  const token = bearerToken?.trim();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

export interface WhipResponseInfo {
  status: number;
  /** fetch followed at least one redirect. */
  redirected: boolean;
  /** Final URL after redirects, when known. */
  url: string;
  hadToken: boolean;
  /** `opaqueredirect` responses carry no status. */
  opaqueRedirect?: boolean;
}

export interface WhipFailure {
  message: string;
  /** Whether trying again later can plausibly succeed without the user changing anything. */
  retryable: boolean;
}

export function describeWhipFailure(info: WhipResponseInfo): WhipFailure {
  const { status } = info;
  const crossOriginHint = info.redirected
    ? ` The request was redirected to ${info.url}; browsers drop the Authorization header on cross-origin redirects, so use that URL as the endpoint.`
    : '';
  if (info.opaqueRedirect || (status >= 300 && status < 400)) {
    return {
      message:
        'The WHIP endpoint answered with a redirect the browser could not follow. Use the final endpoint URL directly.',
      retryable: false,
    };
  }
  switch (status) {
    case 400:
      return { message: 'The WHIP server rejected the offer as malformed (400).', retryable: false };
    case 401:
      return {
        message:
          (info.hadToken
            ? 'The WHIP server rejected the bearer token (401 Unauthorized).'
            : 'The WHIP server requires a bearer token (401 Unauthorized).') + crossOriginHint,
        retryable: false,
      };
    case 403:
      return {
        message: `The bearer token is not allowed to publish to this WHIP endpoint (403 Forbidden).${crossOriginHint}`,
        retryable: false,
      };
    case 404:
      return { message: 'No WHIP endpoint exists at this URL (404 Not Found).', retryable: false };
    case 405:
      return { message: 'This URL does not accept WHIP publishing (405 Method Not Allowed).', retryable: false };
    case 406:
      return {
        message:
          'The WHIP server cannot accept the offered media (406 Not Acceptable); try a different video codec.',
        retryable: false,
      };
    case 409:
      return { message: 'The WHIP server reports a conflicting session for this stream (409).', retryable: true };
    case 415:
      return { message: 'The WHIP server did not accept an SDP offer (415 Unsupported Media Type).', retryable: false };
    case 429:
      return { message: 'The WHIP server is rate limiting this client (429).', retryable: true };
    default:
      if (status >= 500) return { message: `The WHIP server failed (${status}).`, retryable: true };
      return { message: `The WHIP server refused the session (${status}).`, retryable: status === 408 };
  }
}

/** fetch() rejects with a bare TypeError for DNS, TLS, connection and CORS failures alike. */
export const WHIP_NETWORK_FAILURE: WhipFailure = {
  message:
    'Could not reach the WHIP endpoint. Either the network request failed or the server does not allow ' +
    'cross-origin requests from this page (CORS: it must allow POST, the Content-Type and Authorization ' +
    'headers, and expose Location).',
  retryable: true,
};

export interface SdpIceInfo {
  ufrag: string | null;
  pwd: string | null;
  media: Array<{ mid: string; kind: string }>;
}

export function extractIceInfo(sdp: string): SdpIceInfo {
  const info: SdpIceInfo = { ufrag: null, pwd: null, media: [] };
  let kind: string | null = null;
  for (const line of sdp.split(/\r?\n/)) {
    if (line.startsWith('m=')) kind = line.slice(2).split(' ')[0] ?? null;
    else if (line.startsWith('a=mid:') && kind !== null) info.media.push({ mid: line.slice(6), kind });
    else if (line.startsWith('a=ice-ufrag:')) info.ufrag ??= line.slice(12);
    else if (line.startsWith('a=ice-pwd:')) info.pwd ??= line.slice(10);
  }
  return info;
}

/**
 * Body of a trickle-ICE PATCH (`application/trickle-ice-sdpfrag`, RFC 8840):
 * session-level ICE credentials, then per media section a pseudo m-line, its mid
 * and the candidates gathered for it.
 */
export function buildTrickleFragment(
  ice: SdpIceInfo,
  candidates: ReadonlyArray<{ candidate: string; sdpMid: string | null }>,
  endOfCandidates: boolean,
): string | null {
  if (ice.ufrag === null || ice.pwd === null || ice.media.length === 0) return null;
  const fallbackMid = ice.media[0]!.mid;
  const byMid = new Map<string, string[]>();
  for (const c of candidates) {
    if (c.candidate === '') continue;
    const mid = c.sdpMid ?? fallbackMid;
    const line = c.candidate.startsWith('a=') ? c.candidate : `a=${c.candidate}`;
    byMid.set(mid, [...(byMid.get(mid) ?? []), line]);
  }
  if (byMid.size === 0 && !endOfCandidates) return null;
  // With BUNDLE every candidate belongs to the first section, which also carries the end marker.
  if (endOfCandidates && !byMid.has(fallbackMid)) byMid.set(fallbackMid, []);

  const lines = [`a=ice-ufrag:${ice.ufrag}`, `a=ice-pwd:${ice.pwd}`];
  for (const { mid, kind } of ice.media) {
    const own = byMid.get(mid);
    if (!own) continue;
    lines.push(`m=${kind} 9 UDP/TLS/RTP/SAVPF 0`, `a=mid:${mid}`, ...own);
    if (endOfCandidates && mid === fallbackMid) lines.push('a=end-of-candidates');
  }
  return `${lines.join('\r\n')}\r\n`;
}
